import { log } from '@/lib/logger'
import { useState, useEffect, useCallback, useRef } from 'react'
import { conversationService } from '@/services/ConversationService'
import type { ConversationStatus } from '@/services/ConversationService'
import type {
  Conversation,
  ConversationStatusCounts,
  ConversationsListResponse,
  WhatsAppThreadPayload,
} from '@/services/conversationsApi'
import { useComponentLogger } from '@/hooks/use-logger'
import { useContextWebSocket, type MessageData, type ConversationUpdateData } from '@/hooks/use-context-websocket'
import { subscribeToChannelConnectionChanged } from '@/lib/channel-connection-events'

type ConversationWithUnread = Conversation & { unread_count?: number }

export interface UseConversationsOptions {
  /** Uno o varios separados por coma: la pestana Activas es todo menos archivadas. */
  status?: ConversationStatus | string
  source?: string
  category?: string
  tags?: string
  tag_ids?: string
  lead_source?: string
  conversationId?: string
  /** Linea de WhatsApp: solo las conversaciones que entraron por ese numero. */
  phone_number_id?: string
  threads?: 'head' | 'all'
  /** Texto libre: nombre, telefono, email, titulo o cualquier mensaje. Lo resuelve el servidor. */
  search?: string
  /** Etiquetas de la persona (form leads), no de la conversacion. */
  user_tags?: string
  page?: number
  pageSize?: number
}

/** Como se pide una pagina: la primera al cambiar filtros, la siguiente al bajar, o la cabecera al llegar un evento. */
type ModoDeCarga = 'inicial' | 'siguiente' | 'cabecera'

/**
 * Si queda otra pagina, segun lo que diga el servidor.
 *
 * Antes se deducia comparando lo cargado con el total. En una lista que se
 * reordena con cada mensaje, una fila que cambia de pagina entre dos
 * peticiones se pierde al deduplicar y esa cuenta no cuadra nunca: el scroll
 * se quedaba pidiendo paginas vacias para siempre.
 */
export function hayMasPaginas(
  respuesta: Pick<ConversationsListResponse, 'has_next' | 'total_count' | 'count'>,
  pagina: number,
  pageSize: number,
): boolean {
  if (typeof respuesta.has_next === 'boolean') return respuesta.has_next
  const total = respuesta.total_count ?? respuesta.count ?? 0
  return pagina * pageSize < total
}

/**
 * La primera pagina fresca delante y lo que ya estaba cargado detras, sin repetir.
 *
 * Es lo que hace un evento del WebSocket: antes sustituia la lista entera por
 * la primera pagina -esqueleto, scroll arriba, 10 filas- y con 164 eventos
 * por hora nadie pasaba de la cuarta pagina.
 */
export function fusionarCabecera<T extends { id: string }>(actuales: T[], cabecera: T[]): T[] {
  const frescas = new Set(cabecera.map((c) => c.id))
  return [...cabecera, ...actuales.filter((c) => !frescas.has(c.id))]
}

/** La pagina siguiente detras de lo cargado, sin repetir lo que ya estaba. */
export function anexarPagina<T extends { id: string }>(actuales: T[], nuevas: T[]): T[] {
  const vistas = new Set(actuales.map((c) => c.id))
  return [...actuales, ...nuevas.filter((c) => !vistas.has(c.id))]
}

function filasDe(respuesta: ConversationsListResponse | Conversation[]): Conversation[] {
  if (Array.isArray(respuesta)) return respuesta
  if (respuesta.results) return respuesta.results
  if (respuesta.conversations) return Object.values(respuesta.conversations).flat()
  return []
}

interface WebSocketUpdateData {
  status?: ConversationStatus
  ui_status?: ConversationStatus
  updated_at?: string
  message_count?: number
  last_message?: {
    text?: string
    message?: string
    content?: string
    sender?: string
    from?: string
    timestamp?: string
  }
  timestamp?: string
  title?: string
  conversation_title?: string
  description?: string
  metadata?: Record<string, unknown>
  update?: string
  update_type?: string
  intent_status?: string
  chatbot_enabled?: boolean
  chatbot_pause_end?: string
  chatbot_pause_type?: string
  text?: string
  unread_count?: number
  last_activity_at?: string
  [key: string]: unknown
}

export interface UseConversationsReturn {
  conversations: ConversationWithUnread[]
  loading: boolean
  loadingMore: boolean
  error: string | null
  totalCount: number
  hasMore: boolean
  /** Cuantas hay de cada estado con los filtros actuales; alimenta las pestanas. */
  statusCounts: ConversationStatusCounts
  refetch: () => void
  loadMore: () => Promise<void>
  updateStatus: (conversationId: string, status: ConversationStatus) => Promise<void>
  markAsRead: (conversationId: string) => Promise<void>
  removeConversation: (conversationId: string) => Promise<void>
  setFilters: (filters: Partial<UseConversationsOptions>) => void
  // WebSocket status
  isWebSocketConnected: boolean
  webSocketConnectionState: string
  // Update timestamp
  lastUpdateTime: Date | null
  // Indica si hubo cambios reales
  hasRealChanges: boolean
}

/**
 * La tarjeta de ESTE cliente que ya está en la lista, cuando el evento viene de
 * otro de sus hilos.
 *
 * Un contacto con varios números de WhatsApp tiene un hilo por línea y una sola
 * tarjeta. Sin esto, el primer mensaje que entra por la segunda línea inserta
 * una fila nueva y el mismo cliente aparece dos veces hasta la recarga.
 */
export function buscarTarjetaDelMismoCliente<T extends { id: string; source?: string; user?: string }>(
  conversaciones: T[],
  conversationId: string,
  usuario?: string,
): T | undefined {
  if (!usuario) return undefined
  return conversaciones.find(
    (conv) => conv.source === 'WhatsApp' && conv.user === usuario && conv.id !== conversationId,
  )
}

/**
 * La tarjeta con las no leídas de UNO de sus hilos cambiadas.
 *
 * Una tarjeta agrupada suma las no leídas de sus hilos, no las suyas. Sin tocar
 * la entrada del hilo, ni un mensaje nuevo ni abrirlo movían el contador hasta
 * la siguiente recarga, que solo dispara un evento del WebSocket: abrir la
 * línea interna con 3 no leídas las dejaba pintadas.
 */
export function conNoLeidasDelHilo<T extends { whatsapp_threads?: WhatsAppThreadPayload[] | null }>(
  conv: T,
  conversationId: string,
  noLeidas: (actuales: number) => number,
): T {
  const hilos = conv.whatsapp_threads
  if (!hilos?.length || !hilos.some((hilo) => hilo.id === conversationId)) return conv
  return {
    ...conv,
    whatsapp_threads: hilos.map((hilo) =>
      hilo.id === conversationId ? { ...hilo, unread_count: noLeidas(hilo.unread_count || 0) } : hilo,
    ),
  }
}

export function useConversations(initialOptions: UseConversationsOptions = {}): UseConversationsReturn {
  const logger = useComponentLogger('useConversations')
  const AI_STATUS_DISPLAY_MS = 4500
  const WEBSOCKET_REFETCH_DEBOUNCE_MS = 500

  const [conversations, setConversations] = useState<ConversationWithUnread[]>([])
  const [loading, setLoading] = useState(true)
  const [loadingMore, setLoadingMore] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [totalCount, setTotalCount] = useState(0)
  const [hasMore, setHasMore] = useState(false)
  const [statusCounts, setStatusCounts] = useState<ConversationStatusCounts>({})
  const [lastUpdateTime, setLastUpdateTime] = useState<Date | null>(null)
  const [hasRealChanges, setHasRealChanges] = useState(false)

  const [options, setOptions] = useState<UseConversationsOptions>({
    page: 1,
    // 50 y no 10: el servidor admite hasta 100 y con 10 hacian falta 37
    // scrolls para ver la bandeja entera de RK (370 filas).
    pageSize: 50,
    ...initialOptions
  })


  const optionsRef = useRef(options)
  optionsRef.current = options
  const aiStatusTimeoutsRef = useRef<Map<string, ReturnType<typeof setTimeout>>>(new Map())
  const websocketRefetchTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  // La ultima pagina que hay en la lista. Va en un ref y no en estado porque
  // la fija solo la carga de la siguiente: un refresco de cabecera que
  // resolviera antes que una pagina en vuelo la devolvia a 1 y el siguiente
  // scroll pedia la 8 saltandose cinco paginas.
  const pageRef = useRef(1)
  // Sube con cada cambio de filtros: una respuesta de filtros viejos se tira.
  const generationRef = useRef(0)

  const fetchConversations = useCallback(async (modo: ModoDeCarga) => {
    const generacion = modo === 'inicial' ? ++generationRef.current : generationRef.current
    const pagina = modo === 'siguiente' ? pageRef.current + 1 : 1
    const pageSize = optionsRef.current.pageSize || 50

    if (modo === 'inicial') {
      setLoading(true)
      setLoadingMore(false)
    } else if (modo === 'siguiente') {
      setLoadingMore(true)
    }
    setError(null)

    try {
      const currentOptions = optionsRef.current
      const response = await conversationService.listConversations({
        page: pagina,
        pageSize,
        status: currentOptions.status,
        source: currentOptions.source,
        category: currentOptions.category,
        tags: currentOptions.tags,
        tag_ids: currentOptions.tag_ids,
        lead_source: currentOptions.lead_source,
        conversationId: currentOptions.conversationId,
        phone_number_id: currentOptions.phone_number_id,
        threads: currentOptions.threads,
        search: currentOptions.search,
        user_tags: currentOptions.user_tags,
      })
      if (generacion !== generationRef.current) return

      const filas = filasDe(response)
      const total = Array.isArray(response) ? filas.length : (response.total_count ?? response.count ?? filas.length)
      const quedanMas = Array.isArray(response) ? false : hayMasPaginas(response, pagina, pageSize)

      if (modo === 'siguiente') {
        setConversations(prev => anexarPagina(prev, filas))
        pageRef.current = pagina
        setHasMore(quedanMas)
      } else if (modo === 'cabecera') {
        setConversations(prev => fusionarCabecera(prev, filas))
        // Con mas paginas cargadas, si queda otra lo dice la ultima, no esta.
        if (pageRef.current === 1) setHasMore(quedanMas)
        setLastUpdateTime(new Date())
        setHasRealChanges(true)
      } else {
        setConversations(filas)
        pageRef.current = 1
        setHasMore(quedanMas)
        setLastUpdateTime(new Date())
        setHasRealChanges(false)
      }

      setTotalCount(total)
      if (!Array.isArray(response) && response.status_counts) {
        setStatusCounts(response.status_counts)
      }
    } catch (err) {
      if (generacion !== generationRef.current) return
      setError(err instanceof Error ? err.message : 'Failed to fetch conversations')
      if (modo === 'inicial') {
        setConversations([])
      }
    } finally {
      if (generacion === generationRef.current) {
        if (modo === 'inicial') setLoading(false)
        if (modo === 'siguiente') setLoadingMore(false)
      }
    }
  }, [])

  // Función para actualizar una conversación específica localmente
  const updateConversationFromWebSocket = useCallback((conversationId: string, updateData: WebSocketUpdateData) => {
    const normalizedTitle = typeof updateData.title === 'string'
      ? updateData.title
      : (typeof updateData.conversation_title === 'string' ? updateData.conversation_title : undefined)
    const normalizedStatus = (updateData.ui_status as ConversationStatus) || (updateData.status as ConversationStatus)

    setConversations(prev => {
      // Verificar si la conversación existe en la lista actual
      const existingConversation = prev.find(conv => conv.id === conversationId)

      if (existingConversation) {
        const hasNewMessage = Boolean(updateData.last_message)
        const baseMessageCount = existingConversation.message_count || 0
        const nextMessageCount = typeof updateData.message_count === 'number'
          ? updateData.message_count
          : hasNewMessage
            ? baseMessageCount + 1
            : existingConversation.message_count

        const baseUnread = existingConversation.unread_count || 0
        const nextUnreadCount = typeof updateData.unread_count === 'number'
          ? updateData.unread_count
          : hasNewMessage
            ? baseUnread + 1
            : existingConversation.unread_count

        const timestampFromEvent = updateData.last_activity_at || updateData.timestamp || (hasNewMessage ? new Date().toISOString() : undefined)

        // Actualizar conversación existente
        return prev.map(conv => {
          if (conv.id === conversationId) {
            const updatedConversation = {
              ...conv,
              // Update user display name if provided
              ...(updateData.user_display_name ? { user_display_name: updateData.user_display_name as string } : {}),
              // Update source/platform if provided
              ...(updateData.source ? { source: updateData.source as string } : {}),
              ...(updateData.platform ? { platform: updateData.platform as string } : {}),
              // Solo actualizar last_message si se proporciona explícitamente
              ...(updateData.last_message ? {
                last_message: {
                  text: updateData.last_message.text || updateData.last_message.message || updateData.last_message.content || '',
                  sender: updateData.last_message.sender || updateData.last_message.from || 'user',
                  timestamp: updateData.last_message.timestamp || timestampFromEvent || new Date().toISOString()
                }
              } : {}),
              // Solo actualizar message_count si se proporciona explícitamente
              ...(typeof nextMessageCount === 'number' ? { message_count: nextMessageCount } : {}),
              ...(typeof nextUnreadCount === 'number' ? { unread_count: nextUnreadCount } : {}),
              // Actualizar otros campos
              // Permitir actualización del status, incluyendo ai_responding
              ...(normalizedStatus ? { status: normalizedStatus } : {}),
              ...(timestampFromEvent ? { timestamp: timestampFromEvent } : {}),
              ...(normalizedTitle ? { title: normalizedTitle } : {}),
              ...(updateData.description ? { description: updateData.description } : {}),
              ...(updateData.metadata ? { metadata: updateData.metadata } : {}),
              ...(updateData.intent_status ? { metadata: { ...(updateData.metadata || conv.metadata || {}), intent_status: updateData.intent_status } } : {})
            }

            return typeof nextUnreadCount === 'number'
              ? conNoLeidasDelHilo(updatedConversation, conversationId, () => nextUnreadCount)
              : updatedConversation
          }
          return conv
        })
      }

      const tarjetaDelMismoCliente = buscarTarjetaDelMismoCliente(
        prev,
        conversationId,
        typeof updateData.user === 'string' ? updateData.user : undefined,
      )

      if (tarjetaDelMismoCliente) {
        const hayMensaje = Boolean(updateData.last_message)
        return prev.map((conv) =>
          conv.id === tarjetaDelMismoCliente.id
            ? conNoLeidasDelHilo({
                ...conv,
                ...(updateData.last_message
                  ? {
                      last_message: {
                        text:
                          updateData.last_message.text ||
                          updateData.last_message.message ||
                          updateData.last_message.content ||
                          '',
                        sender: updateData.last_message.sender || updateData.last_message.from || 'user',
                        timestamp: updateData.last_message.timestamp || new Date().toISOString(),
                      },
                    }
                  : {}),
                ...(hayMensaje
                  ? { unread_count: (conv.unread_count || 0) + 1 }
                  : {}),
                timestamp: updateData.timestamp || new Date().toISOString(),
              }, conversationId, (actuales) => (hayMensaje ? actuales + 1 : actuales))
            : conv,
        )
      }

      {
        // Si es una nueva conversación, agregarla al inicio de la lista

        const newConversation = {
          id: conversationId,
          user: typeof updateData.user === 'string' ? updateData.user : '',
          client: updateData.client || null,
          user_display_name: typeof updateData.user_display_name === 'string' ? updateData.user_display_name : (typeof updateData.user === 'string' ? updateData.user : 'Usuario'),
          title: normalizedTitle || 'Nueva conversación',
          description: typeof updateData.description === 'string' ? updateData.description : '',
          started_at: typeof updateData.started_at === 'string' ? updateData.started_at : (typeof updateData.timestamp === 'string' ? updateData.timestamp : new Date().toISOString()),
          source: typeof updateData.source === 'string' ? updateData.source : 'unknown',
          platform: typeof updateData.platform === 'string' ? updateData.platform : 'unknown',
          feedback: typeof updateData.feedback === 'string' ? updateData.feedback : '',
          channel_user_id: typeof updateData.channel_user_id === 'string' ? updateData.channel_user_id : '',
          channel_conversation_id: typeof updateData.channel_conversation_id === 'string' ? updateData.channel_conversation_id : '',
          status: normalizedStatus || 'active',
          metadata: updateData.metadata || {},
          timestamp: (typeof updateData.last_activity_at === 'string' && updateData.last_activity_at) || (typeof updateData.timestamp === 'string' && updateData.timestamp) || new Date().toISOString(),
          message_count: typeof updateData.message_count === 'number' ? updateData.message_count : 1,
          unread_count: typeof updateData.unread_count === 'number' ? updateData.unread_count : 1,
          last_message: updateData.last_message ? {
            text: updateData.last_message.text || updateData.last_message.message || updateData.last_message.content || 'Nueva conversación iniciada',
            sender: updateData.last_message.sender || updateData.last_message.from || 'system',
            timestamp: updateData.last_message.timestamp || (typeof updateData.timestamp === 'string' ? updateData.timestamp : new Date().toISOString())
          } : {
            text: 'Nueva conversación iniciada',
            sender: 'system',
            timestamp: typeof updateData.timestamp === 'string' ? updateData.timestamp : new Date().toISOString()
          }
        }

        return [newConversation, ...prev]
      }
    })

    // Marcar que hubo cambios reales
    setHasRealChanges(true)
    setLastUpdateTime(new Date())
  }, [])

  // Función para mostrar estado temporal solo cuando la IA está respondiendo
  const setTemporaryAIStatus = useCallback((conversationId: string, duration: number = AI_STATUS_DISPLAY_MS) => {
    const existingTimeout = aiStatusTimeoutsRef.current.get(conversationId)
    if (existingTimeout) {
      clearTimeout(existingTimeout)
    }

    // Cambiar a estado de IA respondiendo
    setConversations(prev => prev.map(conv =>
      conv.id === conversationId
        ? { ...conv, status: 'ai_responding' as ConversationStatus }
        : conv
    ))

    // Restaurar estado original después del tiempo especificado
    const timeoutId = setTimeout(() => {
      setConversations(prev => prev.map(conv =>
        conv.id === conversationId
          ? { ...conv, status: 'active' as ConversationStatus }
          : conv
      ))
      aiStatusTimeoutsRef.current.delete(conversationId)
    }, duration)

    aiStatusTimeoutsRef.current.set(conversationId, timeoutId)
  }, [AI_STATUS_DISPLAY_MS])

  // Un evento del WebSocket refresca la cabecera EN SITIO: ni esqueleto, ni
  // volver arriba, ni perder lo cargado por scroll.
  const scheduleWebSocketRefetch = useCallback(() => {
    if (websocketRefetchTimeoutRef.current) {
      clearTimeout(websocketRefetchTimeoutRef.current)
    }

    websocketRefetchTimeoutRef.current = setTimeout(() => {
      websocketRefetchTimeoutRef.current = null
      fetchConversations('cabecera')
    }, WEBSOCKET_REFETCH_DEBOUNCE_MS)
  }, [fetchConversations, WEBSOCKET_REFETCH_DEBOUNCE_MS])

  // Configurar WebSocket global para actualizaciones específicas
  const {
    isConnected: isWebSocketConnected,
    connectionState: webSocketConnectionState
  } = useContextWebSocket({
    onNewMessage: useCallback((conversationId?: string, messageData?: MessageData) => {

      if (conversationId && messageData) {
        // Determinar el sender del mensaje
        const messageSender = messageData.sender || 'user'

        // Detectar si es un mensaje de generación de IA
        const isNewMessage = messageData.text || messageData.message || messageData.content
        const isAISender = messageSender === 'ai' || messageSender === 'assistant' || messageSender === 'bot'
        const isGeneratingMessage = messageData.text && (messageData.text.includes('🤖') || messageData.text.includes('Generating'))

        if (isNewMessage && (isAISender || isGeneratingMessage)) {
          setTemporaryAIStatus(conversationId, AI_STATUS_DISPLAY_MS)
        }

        // Actualizar solo la conversación específica
        // Si messageData es un string, tratarlo como el texto del mensaje
        const messageText = typeof messageData === 'string'
          ? messageData
          : (messageData.text || messageData.message || messageData.content || '')


        updateConversationFromWebSocket(conversationId, {
          last_message: {
            text: messageText,
            sender: messageSender,
            timestamp: new Date().toISOString()
          },
          message_count: typeof messageData === 'object' ? messageData.message_count : undefined,
          timestamp: new Date().toISOString(),
          // Include user/platform metadata from WebSocket
          user_display_name: typeof messageData === 'object' ? messageData.user_display_name : undefined,
          source: typeof messageData === 'object' ? messageData.source : undefined,
          platform: typeof messageData === 'object' ? messageData.platform : undefined,
          user: typeof messageData === 'object' ? messageData.user : undefined,
        })
        scheduleWebSocketRefetch()
      } else {
        // Fallback: si no tenemos conversationId, hacer refresh completo
        scheduleWebSocketRefetch()
      }
    }, [updateConversationFromWebSocket, scheduleWebSocketRefetch, setTemporaryAIStatus]),

    onConversationUpdate: useCallback((conversationId?: string, updateData?: ConversationUpdateData) => {
      if (conversationId && updateData) {
        // Si updateData es un string, no tiene información de estado
        if (typeof updateData === 'string') {
          return
        }

        if (typeof updateData === 'object') {
          const wsUpdate = updateData as unknown as WebSocketUpdateData
          const hasStatusUpdate = Boolean(wsUpdate.status || wsUpdate.ui_status)
          const hasTitleUpdate = Boolean(wsUpdate.title || wsUpdate.conversation_title)
          const hasMetadataUpdate = Boolean(wsUpdate.metadata || wsUpdate.intent_status)

          if (!hasStatusUpdate && !hasTitleUpdate && !hasMetadataUpdate) {
            return
          }

          // Si el estado es 'active' después de un mensaje del usuario, asumir que la IA está respondiendo
          const nextStatus = wsUpdate.ui_status || wsUpdate.status
          const shouldShowAIStatus = nextStatus === 'active' &&
            updateData.old_status === 'active' &&
            updateData.new_status === 'active'

          if (shouldShowAIStatus) {
            setTemporaryAIStatus(conversationId, AI_STATUS_DISPLAY_MS)
          }

          // Actualizar solo la conversación específica
          updateConversationFromWebSocket(conversationId, {
            status: nextStatus as ConversationStatus,
            timestamp: updateData.timestamp || wsUpdate.updated_at || new Date().toISOString(),
            last_activity_at: wsUpdate.last_activity_at,
            // Incluir otros campos que puedan haber cambiado
            ...(wsUpdate.title ? { title: wsUpdate.title } : {}),
            ...(wsUpdate.conversation_title ? { conversation_title: wsUpdate.conversation_title as string } : {}),
            ...(wsUpdate.description ? { description: wsUpdate.description } : {}),
            ...(wsUpdate.metadata ? { metadata: wsUpdate.metadata } : {}),
            ...(wsUpdate.intent_status ? { intent_status: wsUpdate.intent_status } : {})
            // NO incluir last_message aquí para evitar sobrescribir
          })
          scheduleWebSocketRefetch()
        }
      } else {
        // Fallback: si no tenemos conversationId, hacer refresh completo
        scheduleWebSocketRefetch()
      }
    }, [updateConversationFromWebSocket, scheduleWebSocketRefetch, setTemporaryAIStatus])
  })

  const setFilters = useCallback((newFilters: Partial<UseConversationsOptions>) => {
    setOptions(prevOptions => {
      const updatedOptions = { ...prevOptions, ...newFilters, page: 1 }
      return updatedOptions
    })
  }, [])

  const refetch = useCallback(() => {
    fetchConversations('inicial')
  }, [fetchConversations])

  useEffect(() => {
    const aiStatusTimeouts = aiStatusTimeoutsRef.current

    return () => {
      aiStatusTimeouts.forEach((timeoutId) => clearTimeout(timeoutId))
      aiStatusTimeouts.clear()
      if (websocketRefetchTimeoutRef.current) {
        clearTimeout(websocketRefetchTimeoutRef.current)
        websocketRefetchTimeoutRef.current = null
      }
    }
  }, [])

  // Trigger fetch when options change (including initial mount)
  useEffect(() => {
    fetchConversations('inicial')
  }, [options.status, options.source, options.category, options.tags, options.tag_ids, options.lead_source, options.conversationId, options.phone_number_id, options.threads, options.search, options.user_tags, options.pageSize, fetchConversations])

  // Keep permissions/status in sync when channel connections change in settings
  useEffect(() => {
    const unsubscribe = subscribeToChannelConnectionChanged(() => {
      conversationService.clearCache()
      fetchConversations('inicial')
    })
    return unsubscribe
  }, [fetchConversations])

  const loadMore = useCallback(async () => {
    if (hasMore && !loadingMore && !loading) {
      await fetchConversations('siguiente')
    }
  }, [hasMore, loadingMore, loading, fetchConversations])

  const updateStatus = useCallback(async (conversationId: string, status: ConversationStatus) => {
    log.debug('🔧 useConversations updateStatus called:', {
      conversationId,
      status,
      currentConversations: conversations.length
    })

    try {
      // Optimistically update the local state first for immediate UI feedback
      setConversations(prev => {
        const updated = prev.map(conv =>
          conv.id === conversationId
            ? { ...conv, status }
            : conv
        )
        log.debug('🔄 Optimistic update applied:', {
          conversationId,
          newStatus: status,
          updatedConversations: updated.length
        })
        return updated
      })

      // Make the API call
      log.debug('📡 Making API call to updateStatus...')
      await conversationService.updateStatus(conversationId, status)
      log.debug('✅ API call successful')

      // No need to refresh - optimistic update is sufficient
      // The server will confirm the change on next manual refresh

    } catch (err) {
      console.error('❌ useConversations updateStatus error:', err)
      logger.error('Error updating conversation status', err)

      // Revert optimistic update on error
      setConversations(prev => {
        const reverted = prev.map(conv =>
          conv.id === conversationId
            ? { ...conv, status: conv.status } // Keep original status
            : conv
        )
        log.debug('🔄 Reverted optimistic update due to error')
        return reverted
      })
      throw err
    }
  }, [logger, conversations])

  const markAsRead = useCallback(async (conversationId: string) => {
    try {
      // Optimistic update
      // También la entrada del hilo dentro de su tarjeta: al abrirlo desde el
      // conmutador, el id no es el de ninguna fila.
      setConversations(prev => prev.map(conv =>
        conNoLeidasDelHilo(
          conv.id === conversationId ? { ...conv, unread_count: 0 } : conv,
          conversationId,
          () => 0,
        )
      ))

      await conversationService.markAsRead(conversationId)
    } catch (err) {
      logger.error('Error marking conversation as read', err)
      // Revert optimistic update if needed, but usually not critical for read status
    }
  }, [logger])

  const removeConversation = useCallback(async (conversationId: string) => {

    // Check if conversation exists in local state first
    const localConversation = conversations.find(conv => conv.id === conversationId)
    if (!localConversation) {
      return // Exit early if not found locally
    }

    try {
      // First call the API to delete it from the server
      await conversationService.deleteConversation(conversationId)

      // Only remove from local state after successful server deletion
      setConversations(prev => prev.filter(conv => conv.id !== conversationId))
      setTotalCount(prev => Math.max(0, prev - 1))
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : String(error)

      // If the error is "Conversation not found", it might already be deleted
      if (errorMessage.includes('Conversation not found')) {
        // Remove from local state anyway since it's already gone from server
        setConversations(prev => prev.filter(conv => conv.id !== conversationId))
        setTotalCount(prev => Math.max(0, prev - 1))
        return
      }

      // For other errors, log as error and don't remove from local state
      logger.error('Error deleting conversation', { conversationId, error: errorMessage })
      throw error
    }
  }, [logger, conversations])

  // Función de debug para probar actualizaciones (solo en desarrollo)
  const debugUpdateConversation = useCallback((conversationId: string, testMessage: string) => {
    if (process.env.NODE_ENV === 'development') {
      updateConversationFromWebSocket(conversationId, {
        last_message: {
          text: testMessage,
          sender: 'user',
          timestamp: new Date().toISOString()
        },
        message_count: (conversations.find(c => c.id === conversationId)?.message_count || 0) + 1,
        timestamp: new Date().toISOString()
      })
    }
  }, [updateConversationFromWebSocket, conversations])

  return {
    conversations,
    loading,
    loadingMore,
    error,
    totalCount,
    hasMore,
    statusCounts,
    refetch,
    loadMore,
    updateStatus,
    markAsRead,
    removeConversation,
    setFilters,
    // WebSocket status
    isWebSocketConnected,
    webSocketConnectionState,
    // Update timestamp
    lastUpdateTime,
    // Indica si hubo cambios reales
    hasRealChanges,
    // Debug function (solo en desarrollo)
    ...(process.env.NODE_ENV === 'development' && { debugUpdateConversation })
  }
}
